import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { 
  FileText, 
  BarChart2, 
  QrCode, 
  Plus, 
  Calendar, 
  TrendingUp,
  Loader
} from 'lucide-react';

interface RecentResponse {
  id: string;
  created_at: string;
  form_id: string;
  viewed_at: string | null;
  forms: {
    id: string;
    name: string;
  }[];
}

const DashboardHomePage = () => {
  const { user, profile } = useAuth();
  const { isOnTrial, daysLeftInTrial, isSubscribed } = useSubscription();
  const [stats, setStats] = useState({
    totalForms: 0,
    activeForms: 0,
    totalResponses: 0,
    recentResponseCount: 0,
  });
  const [recentResponses, setRecentResponses] = useState<RecentResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Fetch forms count
      const { count: formsCount, error: formsError } = await supabase
        .from('forms')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Fetch active QR codes
      const { count: qrCount, error: qrError } = await supabase
        .from('forms')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_active', true);

      // Fetch total responses for user's forms
      const { count: responsesCount, error: responsesError } = await supabase
        .from('responses')
        .select('*, forms!inner(*)', { count: 'exact', head: true })
        .eq('forms.user_id', user.id);

      // Fetch recent responses (last 7 days) for user's forms
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { count: recentCount, error: recentError } = await supabase
        .from('responses')
        .select('*, forms!inner(*)', { count: 'exact', head: true })
        .eq('forms.user_id', user.id)
        .gte('created_at', sevenDaysAgo.toISOString());

      // Fetch recent responses for the list - use a simpler approach like ResponsesPage
      const { data: recentResponsesList, error: recentListError } = await supabase
        .from('responses')
        .select(`
          id, 
          created_at,
          form_id,
          viewed_at
        `)
        .order('created_at', { ascending: false })
        .limit(50); // Get more to filter by user's forms

      // Now fetch forms for this user and match them up
      const { data: userForms, error: userFormsError } = await supabase
        .from('forms')
        .select('id, name')
        .eq('user_id', user.id);

      // Filter responses to only include those from user's forms and add form info
      const filteredResponses = recentResponsesList
        ?.filter(response => 
          userForms?.some(form => form.id === response.form_id)
        )
        .map(response => ({
          ...response,
          forms: userForms?.filter(form => form.id === response.form_id) || []
        }))
        .slice(0, 5); // Take first 5 after filtering

      // Check for errors and log them
      if (formsError) console.error('Forms error:', formsError);
      if (qrError) console.error('QR error:', qrError);
      if (responsesError) console.error('Responses error:', responsesError);
      if (recentError) console.error('Recent error:', recentError);
      if (recentListError) console.error('Recent list error:', recentListError);
      if (userFormsError) console.error('User forms error:', userFormsError);

      if (!formsError && !qrError && !responsesError && !recentError && !recentListError && !userFormsError) {
        console.log('Dashboard data loaded successfully:', {
          formsCount,
          qrCount,
          responsesCount,
          recentCount,
          recentResponsesList,
          userForms,
          filteredResponses
        });
        
        setStats({
          totalForms: formsCount || 0,
          activeForms: qrCount || 0,
          totalResponses: responsesCount || 0,
          recentResponseCount: recentCount || 0,
        });
        setRecentResponses(filteredResponses || []);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const markResponseAsRead = async (responseId: string) => {
    try {
      const { error } = await supabase.rpc('mark_response_as_read', {
        response_id: responseId
      });
      
      if (error) {
        console.error('Error marking response as read:', error);
        return;
      }

      // Update the local state to reflect the change
      setRecentResponses(prev => 
        prev.map(response => 
          response.id === responseId 
            ? { ...response, viewed_at: new Date().toISOString() }
            : response
        )
      );
    } catch (error) {
      console.error('Error marking response as read:', error);
    }
  };

  const getResponseStatus = (response: RecentResponse) => {
    if (!response.viewed_at) {
      return {
        text: 'New Response',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800'
      };
    }
    
    // Check if viewed in the last 24 hours
    const viewedDate = new Date(response.viewed_at);
    const now = new Date();
    const hoursSinceViewed = (now.getTime() - viewedDate.getTime()) / (1000 * 60 * 60);
    
    if (hoursSinceViewed < 24) {
      return {
        text: 'Recently Viewed',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800'
      };
    }
    
    // Old viewed responses don't show a status badge
    return null;
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500">
            Welcome back, {profile?.full_name || 'User'}
          </p>
        </div>

        <div className="mt-4 md:mt-0">
          <Link
            to="/dashboard/forms/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus size={16} className="mr-2" />
            Create New Form
          </Link>
        </div>
      </div>

      {isOnTrial && !isSubscribed && (
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Calendar size={24} className="text-blue-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Trial Period Active
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                <p>
                  You have {daysLeftInTrial} days remaining in your trial. 
                  <Link 
                    to="/dashboard/billing" 
                    className="font-medium text-blue-800 underline ml-1"
                  >
                    Upgrade now
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {/* Total Forms */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <FileText size={24} className="text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Forms
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {isLoading ? '...' : stats.totalForms}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Active QR Codes */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                <QrCode size={24} className="text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active QR Codes
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {isLoading ? '...' : stats.activeForms}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Total Responses */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
                <BarChart2 size={24} className="text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Responses
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {isLoading ? '...' : stats.totalResponses}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Responses */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3">
                <TrendingUp size={24} className="text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Last 7 Days Responses
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {isLoading ? '...' : stats.recentResponseCount}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link 
            to="/dashboard/forms/new" 
            className="relative group bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
          >
            <div>
              <div className="flex items-center">
                <div className="shrink-0 bg-blue-100 rounded-md p-3">
                  <Plus size={20} className="text-blue-600" />
                </div>
                <h3 className="ml-4 text-base font-medium text-gray-900">Create a New Form</h3>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Build a custom feedback form with various question types
              </p>
            </div>
          </Link>

          <Link 
            to="/dashboard/responses" 
            className="relative group bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
          >
            <div>
              <div className="flex items-center">
                <div className="shrink-0 bg-indigo-100 rounded-md p-3">
                  <BarChart2 size={20} className="text-indigo-600" />
                </div>
                <h3 className="ml-4 text-base font-medium text-gray-900">View Responses</h3>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                See all feedback submitted through your forms
              </p>
            </div>
          </Link>

          <Link
            to="/dashboard/order-stickers"
            className="relative group bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
          >
            <div>
              <div className="flex items-center">
                <div className="shrink-0 bg-green-100 rounded-md p-3">
                  <QrCode size={20} className="text-green-600" />
                </div>
                <h3 className="ml-4 text-base font-medium text-gray-900">Design & Order</h3>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Design and order QR code stickers for your business
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent responses */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-900">Recent Responses</h2>
          <Link 
            to="/dashboard/responses"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            View all
          </Link>
        </div>
        
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {isLoading ? (
            <div className="py-12 text-center text-gray-500">
              <Loader size={24} className="animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : recentResponses.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {recentResponses.map((response) => {
                const status = getResponseStatus(response);
                return (
                  <li key={response.id}>
                    <Link 
                      to={`/dashboard/responses/${response.forms[0]?.id || response.form_id}`}
                      className="block hover:bg-gray-50"
                      onClick={() => markResponseAsRead(response.id)}
                    >
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-blue-600 truncate">
                            {response.forms[0]?.name || 'Unknown Form'}
                          </p>
                          {status && (
                            <div className="ml-2 flex-shrink-0 flex">
                              <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.bgColor} ${status.textColor}`}>
                                {status.text}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="mt-2 flex justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              {formatDate(response.created_at)}
                              {response.viewed_at && (
                                <span className="ml-2 text-xs text-gray-400">
                                  • Viewed {formatDate(response.viewed_at)}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="py-12 text-center">
              <QrCode size={48} className="mx-auto text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No responses yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Create a form and share it to start collecting feedback.
              </p>
              <div className="mt-6">
                <Link
                  to="/dashboard/forms/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-2" />
                  Create New Form
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHomePage;